import React, { ErrorInfo, ReactNode } from "react";
import { bizError } from "../utils/debugLogger";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[REACT_RENDER_ERROR]", error, errorInfo);
    bizError("REACT_RENDER_ERROR", {
      message: error?.message || error?.toString(),
      stack: error?.stack,
      componentStack: errorInfo?.componentStack
    });
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div className="p-6 max-w-lg mx-auto my-8 bg-rose-50 border border-rose-200 rounded-2xl text-rose-950 font-sans shadow-md space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-2xl">⚠️</span>
            <h2 className="text-base font-black uppercase tracking-wide">Something went wrong</h2>
          </div>
          <p className="text-xs font-semibold text-rose-700 leading-relaxed">
            A rendering error occurred in the user interface. Our engineering agent has been notified.
          </p>
          <pre className="p-3 bg-rose-950 text-rose-100 rounded-xl text-[10px] font-mono overflow-auto max-h-40 border border-rose-900 leading-normal">
            {this.state.error?.toString() || "Unknown rendering error"}
          </pre>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white font-black text-xs rounded-xl transition cursor-pointer"
          >
            Reload Workspace
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
